import React, { useState, useEffect } from 'react';
import { useApps } from '@/contexts/AppsContext';
import { useAppId } from '@/hooks/useAppId';
import { apiKeysApi } from '@/services/api';
import { API_CONFIG } from '@/config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Send,
  Key,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  Plus,
  FileText,
  Image,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';

const CONTENT_TYPES = [
  { value: 'text/plain', label: 'Plain Text', icon: FileText },
  { value: 'text/markdown', label: 'Markdown', icon: FileText },
  { value: 'text/html', label: 'HTML', icon: FileText },
  { value: 'text/csv', label: 'CSV', icon: FileText },
];

export default function IngestPage() {
  const appId = useAppId();
  const { currentApp } = useApps();

  // API key state
  const [apiKeys, setApiKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);

  // Ingest form state
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState('text/plain');
  const [authorHint, setAuthorHint] = useState('');
  const [sourceMedium, setSourceMedium] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Response state
  const [lastResponse, setLastResponse] = useState(null);
  const [ingestHistory, setIngestHistory] = useState([]);

  // Fetch keys on mount
  useEffect(() => {
    const fetchKeys = async () => {
      if (!appId) {
        setKeysLoading(false);
        return;
      }
      try {
        const response = await apiKeysApi.list(appId);
        const keys = response.data.data?.keys || [];
        setApiKeys(keys.filter((k) => !k.revoked));
      } catch (err) {
        console.error('Failed to fetch keys:', err);
      } finally {
        setKeysLoading(false);
      }
    };
    fetchKeys();
  }, [appId]);

  // Auto-create an API key if none exist
  const handleCreateKey = async () => {
    if (!appId) return;
    setCreatingKey(true);
    try {
      const response = await apiKeysApi.create(appId, { name: `Admin Ingest Key (${new Date().toLocaleDateString()})` });
      const newKey = response.data.data;
      if (newKey?.plainKey) {
        setApiKey(newKey.plainKey);
        toast.success('API key created. It has been auto-filled below.');
        // Refresh keys list
        const keysRes = await apiKeysApi.list(appId);
        setApiKeys((keysRes.data.data?.keys || []).filter((k) => !k.revoked));
      }
    } catch (err) {
      toast.error('Failed to create API key: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingKey(false);
    }
  };

  // Submit content to data plane
  const handleIngest = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter or create an API key first.');
      return;
    }
    if (!content.trim()) {
      toast.error('Please enter some content to ingest.');
      return;
    }

    setSubmitting(true);
    setLastResponse(null);

    const startTime = Date.now();

    try {
      const body = {
        content: content.trim(),
        content_type: contentType,
        metadata: {},
      };
      if (authorHint.trim()) body.metadata.author_hint = authorHint.trim();
      if (sourceMedium.trim()) body.metadata.source_medium = sourceMedium.trim();

      const res = await fetch(`${API_CONFIG.dataPlaneUrl}/v1/ingest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey.trim(),
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      const elapsed = Date.now() - startTime;

      const entry = {
        timestamp: new Date().toISOString(),
        status: res.status,
        ok: res.ok,
        elapsed,
        contentPreview: content.trim().slice(0, 80) + (content.trim().length > 80 ? '...' : ''),
        contentType,
        response: data,
      };

      setLastResponse(entry);
      setIngestHistory((prev) => [entry, ...prev].slice(0, 20));

      if (res.ok) {
        toast.success(`Content accepted (${elapsed}ms)`);
        setContent('');
      } else {
        toast.error(`Ingest failed: ${data.message || data.error || res.statusText}`);
      }
    } catch (err) {
      const elapsed = Date.now() - startTime;
      const entry = {
        timestamp: new Date().toISOString(),
        status: 0,
        ok: false,
        elapsed,
        contentPreview: content.trim().slice(0, 80),
        contentType,
        response: { error: err.message },
      };
      setLastResponse(entry);
      setIngestHistory((prev) => [entry, ...prev].slice(0, 20));
      toast.error('Network error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentApp) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <div className="text-center space-y-2">
          <Upload className="h-10 w-10 mx-auto opacity-50" />
          <p className="text-sm">Select an app to ingest content.</p>
          <p className="text-xs">Use the App Inspector dropdown in the sidebar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Ingest Content</h2>
        <p className="text-muted-foreground">
          Submit content to <span className="text-foreground font-medium">{currentApp.name}</span>'s
          data plane for extraction and processing.
        </p>
      </div>

      {/* API Key Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Key className="h-4 w-4" />
            API Key
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="Paste an API key for this app..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="font-mono text-sm"
            />
            {apiKey && (
              <Badge variant="outline" className="shrink-0 self-center text-emerald-400 border-emerald-400/30">
                Set
              </Badge>
            )}
          </div>

          {keysLoading ? (
            <p className="text-xs text-muted-foreground">Loading keys...</p>
          ) : apiKeys.length === 0 ? (
            <div className="flex items-center gap-2 text-xs">
              <AlertTriangle className="h-3 w-3 text-amber-400" />
              <span className="text-amber-400">This app has no active API keys.</span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs"
                onClick={handleCreateKey}
                disabled={creatingKey}
              >
                {creatingKey ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Plus className="mr-1 h-3 w-3" />
                )}
                Create One
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {apiKeys.length} active key{apiKeys.length !== 1 ? 's' : ''} for this app.
              Paste the full key value above (key prefixes: {apiKeys.map((k) => k.keyPrefix).join(', ')}).
            </p>
          )}
        </CardContent>
      </Card>

      {/* Content Input */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Content
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Content type + metadata row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Content Type</Label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map((ct) => (
                    <SelectItem key={ct.value} value={ct.value} className="text-xs">
                      {ct.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Author Hint</Label>
              <Input
                className="h-8 text-xs"
                placeholder="e.g., john@acme.com"
                value={authorHint}
                onChange={(e) => setAuthorHint(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Source Medium</Label>
              <Input
                className="h-8 text-xs"
                placeholder="e.g., email, slack, web"
                value={sourceMedium}
                onChange={(e) => setSourceMedium(e.target.value)}
              />
            </div>
          </div>

          {/* Text area */}
          <textarea
            className="w-full min-h-[200px] rounded-lg border border-border bg-background px-4 py-3 text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-y"
            placeholder="Paste or type content to ingest...&#10;&#10;This will be sent to the data plane for extraction. The ingest worker will parse it, extract concepts, people, and emotional signatures, write them to the graph, and create heartbeat events for the consciousness layer to process."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />

          {/* Character count + Submit */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {content.length.toLocaleString()} characters
            </span>
            <Button
              onClick={handleIngest}
              disabled={submitting || !content.trim() || !apiKey.trim()}
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {submitting ? 'Ingesting...' : 'Submit to Data Plane'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Last Response */}
      {lastResponse && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              {lastResponse.ok ? (
                <CheckCircle className="h-4 w-4 text-emerald-400" />
              ) : (
                <XCircle className="h-4 w-4 text-red-400" />
              )}
              Last Response
              <Badge variant={lastResponse.ok ? 'outline' : 'destructive'} className="text-xs">
                {lastResponse.status} — {lastResponse.elapsed}ms
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted/50 rounded-lg p-3 overflow-auto max-h-48 font-mono">
              {JSON.stringify(lastResponse.response, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Ingest History */}
      {ingestHistory.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ingestHistory.map((entry, i) => (
                <div
                  key={entry.timestamp + i}
                  className="flex items-center gap-3 text-xs py-1.5 border-b border-border/50 last:border-0"
                >
                  {entry.ok ? (
                    <CheckCircle className="h-3 w-3 text-emerald-400 shrink-0" />
                  ) : (
                    <XCircle className="h-3 w-3 text-red-400 shrink-0" />
                  )}
                  <span className="text-muted-foreground w-16 shrink-0">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {entry.contentType.split('/')[1]}
                  </Badge>
                  <span className="truncate text-muted-foreground">{entry.contentPreview}</span>
                  <span className="ml-auto text-muted-foreground shrink-0">{entry.elapsed}ms</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
